<template>
	<v-row class="full-height supplier-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="supplier-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="4" lg="3">
							<v-text-field
								v-model="searchForm.keywords"
								placeholder="Style Code, Description, Vendor Name"
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="supplier-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<!-- Filter Inactive -->
						<v-col cols="auto">
							<v-checkbox
								class="mt-0 pt-0"
								v-model="searchForm.filterInactive"
								label="Filter Inactive"
								color="gray"
								hide-details
								@change="onFilterChange"
							></v-checkbox>
						</v-col>

						<!-- Spacer -->
						<v-spacer class="d-none d-md-flex"></v-spacer>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 supplier-table-container">
			<v-data-table
				:headers="headers"
				:items="supplierItems"
				:loading="loading"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="supplier-data-table"
				must-sort
				fixed-header
			>
				<!-- Default Vendor Column -->
				<template v-slot:item.isDefault="{ item }">
					<v-checkbox
						class="form-checkbox-center"
						:input-value="item.isDefault"
						color="accent"
						hide-details
						readonly
					></v-checkbox>
				</template>

				<!-- Vendor Cost Column -->
				<template v-slot:item.vendorCost="{ item }">
					{{ renderPrice(item.vendorCost) }}
				</template>

				<!-- Actions Column -->
				<template v-slot:item.actions="{ item }">
					<v-btn
						icon
						color="primary"
						@click="editSupplier(item)"
						:disabled="!canUpdateSupplier()"
					>
						<v-icon style="font-size: 2rem">mdi-pencil</v-icon>
					</v-btn>
					<v-btn
						icon
						color="primary"
						@click="deleteSupplier(item)"
						:disabled="!canDeleteSupplier() || item.isDefault"
					>
						<v-icon style="font-size: 2rem">mdi-delete</v-icon>
					</v-btn>
					<v-btn
						depressed
						outlined
						small
						@click="newSupplier(item)"
						:disabled="!canUpdateSupplier()"
					>
						Add Supplier
					</v-btn>
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-truck-delivery</v-icon>
						<p class="mt-2 grey--text">No suppliers found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>

		<v-overlay v-if="showSupplierModal" :dark="false" :absolute="true">
			<div class="input-modal supplier-modal">
				<div class="input-modal-header">
					<h1 class="input-modal-header-title">Supplier</h1>
					<v-btn icon @click="showSupplierModal = false">
						<v-icon v-text="'mdi-close'"></v-icon>
					</v-btn>
				</div>
				<div class="input-modal-content">
					<div class="supplier-modal-form">
						<div class="form-input">
							<label>Style Code:</label>
							<v-text-field
								outlined
								dense
								hide-details="auto"
								v-model="activeSupplier.styleCode"
								disabled
							></v-text-field>
						</div>
						<div class="form-input">
							<label>Description:</label>
							<v-text-field
								outlined
								dense
								hide-details="auto"
								v-model="activeSupplier.description"
								disabled
							></v-text-field>
						</div>
						<div class="form-input">
							<label>Vendor:</label>

							<div class="search-input">
								<v-text-field
									prepend-inner-icon="mdi-magnify"
									outlined
									dense
									hide-details="auto"
									v-model="activeSupplier.vendor.companyName"
									clearable
									@keyup="searchVendors()"
									:disabled="
										!activeSupplier.isDefault &&
										activeSupplier.vendor.vendorId !== null &&
										activeSupplier.vendorId !== activeSupplier.vendor.vendorId
									"
								></v-text-field>

								<div v-if="showSupplierSearch" class="search-input-dropdown">
									<div class="search-input-dropdown-results">
										<ul>
											<li v-if="searchResults.length == 0">No Results Found</li>
											<li
												v-for="(res, resIndex) in searchResults"
												:key="resIndex"
												@click="setVendor(res)"
											>
												<p>{{ res.companyName }}</p>
											</li>
										</ul>
									</div>
								</div>
							</div>
						</div>
						<div class="form-input">
							<label class="mb-2">Default Vendor:</label>
							<v-checkbox
								class="form-checkbox-center"
								v-model="activeSupplier.isDefault"
								color="accent"
								hide-details="auto"
								:readonly="activeSupplier.vendorId == activeSupplier.vendor.vendorId"
							></v-checkbox>
						</div>
						<div class="form-input">
							<label>Vendor #:</label>
							<v-text-field
								outlined
								dense
								hide-details="auto"
								v-model="activeSupplier.vendor.vendorId"
								disabled
							></v-text-field>
						</div>
						<div class="form-input">
							<label>Vendor Part Number:</label>
							<v-text-field
								outlined
								dense
								hide-details="auto"
								v-model="activeSupplier.vendorPartNumber"
							></v-text-field>
						</div>
						<div class="form-input">
							<label>Vendor Cost:</label>
							<currency-text-field v-model="activeSupplier.vendorCost" />
						</div>
						<div class="form-input">
							<label>Lead Time:</label>
							<v-text-field
								class="number-input"
								outlined
								dense
								hide-details="auto"
								v-model="activeSupplier.leadTime"
								type="number"
								min="0"
							></v-text-field>
						</div>
					</div>

					<div class="input-modal-content-actions">
						<v-btn
							outlined
							depressed
							@click="showSupplierModal = false"
							:disabled="!canUpdateSupplier()"
							>Cancel</v-btn
						>
						<v-btn
							color="primary"
							depressed
							@click="updateSupplier()"
							:disabled="!canUpdateSupplier()"
							>Save</v-btn
						>
					</div>
				</div>
			</div>
		</v-overlay>
	</v-row>
</template>

<script>
import { GET_ALL_INVENTORY_ITEMS_SUPPLIER } from "@/gql/inventory";
import { SEARCH_VENDORS } from "@/gql/vendors";
import { renderPrice } from "@/helpers/PricingCalculator";
import { hasPermission, SystemPermissions } from "@/models/User";

export default {
	name: "Supplier",
	data() {
		return {
			loading: true,
			searchForm: {
				keywords: "",
				filterInactive: false,
			},
			showSupplierModal: false,
			activeSupplier: {},
			showSupplierSearch: false,
			searchResults: [],
			supplierItems: [],
			totalCount: 0,
			searchTimeout: null,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["styleCode"],
				sortDesc: [false],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			headers: [
				{
					text: "Style Code",
					value: "styleCode",
					sortable: true,
					width: "9%",
				},
				{
					text: "Description",
					value: "description",
					sortable: true,
					width: "20%",
				},
				{
					text: "Vendor Name",
					value: "vendorName",
					sortable: true,
					width: "12%",
				},
				{
					text: "Vendor #",
					value: "vendorDisplayId",
					sortable: true,
					width: "8%",
				},
				{
					text: "Default Vendor",
					value: "isDefault",
					sortable: false,
					width: "9%",
					align: "center",
				},
				{
					text: "Vendor Part #",
					value: "vendorPartNumber",
					sortable: true,
					width: "9%",
				},
				{
					text: "Vendor Cost",
					value: "vendorCost",
					sortable: true,
					width: "9%",
				},
				{
					text: "Lead Time",
					value: "leadTime",
					sortable: true,
					width: "6%",
				},
				{
					text: "",
					value: "actions",
					sortable: false,
					width: "18%",
				},
			],
		};
	},
	apollo: {
		supplierQuery: {
			query: GET_ALL_INVENTORY_ITEMS_SUPPLIER,
			variables() {
				return this.buildQueryVariables();
			},
			update(data) {
				this.loading = false;
				this.totalCount = data.response?.totalCount || 0;
				// Flatten items and their suppliers
				this.supplierItems = this.flattenItems(data.response?.items || []);
				return data.response;
			},
			error(error) {
				console.error("Error fetching suppliers:", error);
				this.loading = false;
			},
		},
	},
	computed: {
		navigationDirection() {
			return this.$store.getters["globalState/navigationDirection"];
		},
	},
	watch: {
		tableOptions: {
			handler() {
				this.fetchData();
			},
			deep: true,
		},
		navigationDirection: {
			handler(newDirection) {
				if (newDirection) {
					const savedState = this.$store.getters["globalState/supplierState"];
					if (savedState) {
						// Restore page from offset
						const page = Math.floor(savedState.offset / this.tableOptions.itemsPerPage) + 1;
						this.tableOptions.page = page;
						this.searchForm.keywords = savedState.searchTerm || "";
						this.searchForm.filterInactive = savedState.filterInactive || false;
					}
				}
			},
			immediate: true,
		},
	},
	methods: {
		canUpdateSupplier() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.Purchasing_Supplier_Update)) {
				return true;
			}
			return user.isAdmin;
		},

		canDeleteSupplier() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.Purchasing_Supplier_Delete)) {
				return true;
			}
			return user.isAdmin;
		},

		renderPrice,

		// Map table column values to GraphQL sort field names
		getSortFieldName(columnValue) {
			const sortFieldMap = {
				styleCode: "styleCode",
				description: "description",
				vendorName: "vendor.companyName",
				vendorDisplayId: "vendor.vendorId",
				vendorPartNumber: "vendorPartNumber",
				vendorCost: "vendorCost",
				leadTime: "leadTime",
				createDate: "createDate",
			};
			return sortFieldMap[columnValue] || "createDate";
		},

		// Build nested sort object from dot notation (e.g., "vendor.companyName")
		buildSortObject(fieldPath, direction) {
			const parts = fieldPath.split(".");
			if (parts.length === 1) {
				return { [parts[0]]: direction };
			}
			// Build nested object from inside out
			let result = { [parts[parts.length - 1]]: direction };
			for (let i = parts.length - 2; i >= 0; i--) {
				result = { [parts[i]]: result };
			}
			return result;
		},

		buildQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
			const keyword = (this.searchForm.keywords || "").trim();

			let where = {};
			if (keyword) {
				where = {
					or: [
						{ styleCode: { contains: keyword } },
						{ description: { contains: keyword } },
						{
							vendor: {
								companyName: { contains: keyword },
							},
						},
						{
							suppliers: {
								some: {
									vendor: {
										companyName: { contains: keyword },
									},
								},
							},
						},
					],
				};
			}

			if (this.searchForm.filterInactive) {
				where.inactiveDate = { neq: null };
			} else {
				where.inactiveDate = { eq: null };
			}

			// Build dynamic sort order
			const sortColumn = sortBy[0] || "styleCode";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const sortFieldPath = this.getSortFieldName(sortColumn);
			const order = [this.buildSortObject(sortFieldPath, sortDirection)];

			return {
				offset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
				where,
			};
		},

		flattenItems(items) {
			const result = [];
			items.forEach((item) => {
				// Add the main item
				result.push({
					inventoryItemId: item.inventoryItemId,
					styleCode: item.styleCode,
					description: item.description,
					leadTime: item.leadTime,
					vendorCost: item.vendorCost,
					vendorId: item.vendorId,
					vendorPartNumber: item.vendorPartNumber,
					vendorName: item.vendor?.companyName || "",
					vendorDisplayId: item.vendor?.vendorId,
					isDefault: true,
					vendor: item.vendor,
				});

				// Add supplier items
				if (item.suppliers && item.suppliers.length > 0) {
					item.suppliers.forEach((sItem) => {
						result.push({
							inventoryItemId: item.inventoryItemId,
							styleCode: item.styleCode,
							description: item.description,
							leadTime: sItem.leadTimeInWeeks,
							vendorCost: sItem.cost,
							vendorId: item.vendorId,
							vendorPartNumber: sItem.vendorPartNumber,
							vendorName: sItem.vendor?.companyName || "",
							vendorDisplayId: sItem.vendorId,
							isDefault: false,
							vendor: {
								vendorId: sItem.vendorId,
								companyName: sItem.vendor?.companyName || "",
							},
						});
					});
				}
			});
			return result;
		},

		async fetchData() {
			this.loading = true;
			if (this.$apollo.queries.supplierQuery) {
				await this.$apollo.queries.supplierQuery.refetch();
			}
		},

		onSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			}, 400);
		},

		onSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.tableOptions.page = 1;
			this.fetchData();
		},

		onFilterChange() {
			this.tableOptions.page = 1;
			this.fetchData();
		},

		newSupplier(item) {
			let defaultSupplier = {
				inventoryItemId: item.inventoryItemId,
				styleCode: item.styleCode,
				description: item.description,
				vendorId: item.vendorId,
				vendor: {
					vendorId: null,
					companyName: "",
				},
				vendorPartNumber: "",
				vendorCost: 0.0,
				leadTime: 0,
				isDefault: false,
			};

			this.activeSupplier = defaultSupplier;
			this.showSupplierModal = true;
		},

		editSupplier(item) {
			this.activeSupplier = {
				...item,
				isDefault: item.vendorId == item.vendor?.vendorId ? true : false,
			};
			this.showSupplierModal = true;
		},

		setVendor(vendor) {
			this.activeSupplier.vendor.vendorId = vendor.vendorId;
			this.activeSupplier.vendor.companyName = vendor.companyName;

			//reset the search display
			this.showSupplierSearch = false;
		},

		async searchVendors() {
			try {
				const res = await this.$apollo.query({
					fetchPolicy: "no-cache",
					query: SEARCH_VENDORS,
					variables: {
						searchTerm: `${this.activeSupplier.vendor.companyName}`,
					},
				});

				//replace with res.data once api is setup
				this.searchResults = res.data.response.items;
				this.showSupplierSearch = true;
			} catch (error) {
				console.log(error);
			}
		},

		async deleteSupplier(data) {
			try {
				if (data.isDefault) {
					console.log("cannot delete default suppliers");
					return;
				} else {
					const res = await this.$http.delete(
						`/api/inventory/items/${data.inventoryItemId}/suppliers/${data.vendor.vendorId}`
					);
					console.log(res);
				}
				console.log("deleting item", data);
				//refresh latest items
				this.tableOptions.page = 1;
				this.fetchData();
			} catch (error) {
				console.log(error);
			}
		},

		async updateSupplier() {
			try {
				let supplier = {
					vendorId: this.activeSupplier.vendor.vendorId,
					vendorPartNumber: this.activeSupplier.vendorPartNumber,
					vendorCost: this.activeSupplier.vendorCost,
					isDefault: this.activeSupplier.isDefault,
					leadTimeInWeeks: this.activeSupplier.leadTime,
				};

				const res = await this.$http.put(
					`/api/inventory/items/${this.activeSupplier.inventoryItemId}/suppliers/${supplier.vendorId}`,
					supplier
				);
				console.log(res);

				//refresh latest items terms and clear activeSupplier
				this.showSupplierModal = false;
				this.activeSupplier = null;
				this.fetchData();
			} catch (error) {
				console.log(error);
			}
		},
	},
	beforeRouteLeave(to, from, next) {
		const offset = (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage;
		this.$store.dispatch("globalState/saveSupplierState", {
			searchTerm: this.searchForm.keywords,
			filterInactive: this.searchForm.filterInactive,
			offset,
			scrollPosition: window.scrollY,
		});
		next();
	},

	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
};
</script>

<style lang="scss" scoped>
.supplier-page {
	// Use calc to fit within viewport: 100vh - header (64px) - container padding (24px)
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.supplier-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;
}

.supplier-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0; // Don't shrink the toolbar
}

.supplier-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.supplier-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;

	// Table wrapper should scroll
	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;
		overflow-x: auto;

		table {
			min-width: 1100px;
		}
	}

	// Keep footer fixed at bottom
	::v-deep .v-data-footer {
		flex-shrink: 0;
	}

	// Match existing header styling
	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	// Match existing row height and font size
	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
	}

	// Footer styling
	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}
}

.supplier-modal {
	&-form {
		display: flex;
		flex-flow: column wrap;

		.form-input {
			margin: 0.75rem 0;

			&:last-of-type {
				.v-input {
					width: 12rem;
					flex: initial;
				}
			}
		}
	}
}

.search-input {
	position: relative;
	display: flex;
	width: 60%;

	&-dropdown {
		position: absolute;
		left: 0;
		bottom: -28.5rem; //height of the filter + extra spacing
		background-color: $color-white;
		border-radius: 3px;
		width: 40vw;
		max-width: 40vw;
		height: 28rem;
		box-shadow: rgba(50, 50, 93, 0.15) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px;
		z-index: 1000;
		overflow-y: auto;
		overflow-x: hidden;

		&-results {
			ul {
				list-style: none;
				padding-left: 0;

				li {
					display: flex;
					flex-flow: row wrap;
					align-items: center;
					font-size: 1.3rem;
					white-space: nowrap;
					transition: all 0.4s;
					padding: 0.8rem;
					padding-left: 1rem;

					&:hover {
						cursor: pointer;
						background-color: $color-gray-light;
					}

					p {
						font-size: 1.4rem;
						font-weight: normal;
						text-transform: uppercase;
					}
				}
			}
		}
	}
}
</style>
